import { callApiV2 } from "@mirage/service-dbx-api";

import type { files, previews, transcript_edit } from "@dropbox/api-v2-client";
import type { ApiV2ClientShim } from "@dropbox/file-viewer/packaging/apiv2_client_shim";

const isUrl = (url: string | undefined): boolean => {
  if (!url) {
    return false;
  }
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const maybeProxyUrl = <T extends string | undefined>(field: T): T | string => {
  return window.location.origin.includes("http://localhost") && isUrl(field)
    ? `${window.location.origin}/preview_proxy/${field}`
    : field;
};

const proxyFields = <T extends string | undefined>(
  fields: Record<string, T | string>,
) => {
  return Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [key, maybeProxyUrl(value)]),
  );
};

/**
 * Riviera, the service which provides previews of Dropbox FSS content, requires auth. It maintains
 * an allowlist of origins that can access preview content. While Dash's production and staging URLs
 * are on the allowlist, localhost can't be.
 *
 * This function proxies previews URLs so that we can intercept them with the proxy defined in craco.config.js.
 * Proxying allows us to append CORS headers to the response when developing against localhost. The APIv2 response
 * is modified in-place.
 *
 * This is a no-op when accessed from an origin other than localhost.
 */
const proxyPreviewMetadata = (
  previewMetadata: previews.PreviewMetadata | undefined,
): previews.PreviewMetadata | undefined => {
  if (
    !previewMetadata ||
    !window.location.origin.includes("http://localhost")
  ) {
    return previewMetadata;
  }

  previewMetadata.preview_url = maybeProxyUrl(previewMetadata.preview_url);

  if (!previewMetadata.content) {
    return previewMetadata.content;
  }

  switch (previewMetadata.content[".tag"]) {
    case "excel":
      previewMetadata.content = {
        ...previewMetadata.content,
        ...proxyFields({
          workbook_data_url_tmpl:
            previewMetadata.content.workbook_data_url_tmpl,
          sheet_data_url_tmpl: previewMetadata.content.sheet_data_url_tmpl,
          tile_url_tmpl: previewMetadata.content.tile_url_tmpl,
        }),
      };
      break;
    case "video":
      previewMetadata.content = {
        ...previewMetadata.content,
        ...proxyFields({
          transcode_url: previewMetadata.content.transcode_url,
          transcript_url: previewMetadata.content.transcript_url,
          metadata_url: previewMetadata.content.metadata_url,
          thumb_scrubber_vtt_url:
            previewMetadata.content.thumb_scrubber_vtt_url,
          poster_url_tmpl: previewMetadata.content.poster_url_tmpl,
          generated_captions_url:
            previewMetadata.content.generated_captions_url,
        }),
        transcript_urls_by_lang: proxyFields(
          previewMetadata.content.transcript_urls_by_lang ?? {},
        ),
        caption_urls_by_lang: proxyFields(
          previewMetadata.content.caption_urls_by_lang ?? {},
        ),
      };
      break;
    case "audio":
      previewMetadata.content = {
        ...previewMetadata.content,
        ...proxyFields({
          transcript_url: previewMetadata.content.transcript_url,
          generated_captions_url:
            previewMetadata.content.generated_captions_url,
          transcode_url: previewMetadata.content.transcode_url,
          waveform_url: previewMetadata.content.waveform_url,
        }),
        transcript_urls_by_lang: proxyFields(
          previewMetadata.content.transcript_urls_by_lang ?? {},
        ),
        caption_urls_by_lang: proxyFields(
          previewMetadata.content.caption_urls_by_lang ?? {},
        ),
      };
      break;
    case "linkfile":
      previewMetadata.content = {
        ...previewMetadata.content,
        url: maybeProxyUrl(previewMetadata.content.url),
      };
      break;
    case "ssr_doc":
    case "cloud_doc":
      previewMetadata.content = {
        ...previewMetadata.content,
        ...proxyFields({
          text_url_tmpl: previewMetadata.content.text_url_tmpl,
          image_url_tmpl: previewMetadata.content.image_url_tmpl,
          refresh_url: previewMetadata.content.refresh_url,
          placeholder_image_url: previewMetadata.content.placeholder_image_url,
          autoprint_url: previewMetadata.content.autoprint_url,
        }),
      };
      break;
    case "image":
      previewMetadata.content = {
        ...previewMetadata.content,
        ...proxyFields({
          thumbnail_url_tmpl: previewMetadata.content.thumbnail_url_tmpl,
          default_src: previewMetadata.content.default_src,
          full_size_src: previewMetadata.content.full_size_src,
          src_set: previewMetadata.content.src_set,
        }),
      };
      break;
    case "text":
      previewMetadata.content = {
        ...previewMetadata.content,
        ...proxyFields({
          extracted_text: previewMetadata.content.extracted_text,
        }),
      };
      break;
    case "three_dimensional":
      previewMetadata.content = {
        ...previewMetadata.content,
        ...proxyFields({
          model_url: previewMetadata.content.model_url,
        }),
      };
      break;
    case "archive":
    case "raw_html":
    case "other":
    case "restricted":
      break;
    default:
      previewMetadata.content[".tag"] satisfies never;
      break;
  }

  return previewMetadata;
};

export const makeApiV2ClientShim = (): ApiV2ClientShim => {
  return {
    filesGetFileContentMetadata: (arg: files.GetFileContentMetadataArg) =>
      callApiV2("filesGetFileContentMetadata", arg),
    filesGetMetadataBatch: (arg: files.GetMetadataBatchArg) =>
      callApiV2("filesGetMetadataBatch", arg),
    mediaAddonGetFeatures: () => callApiV2("mediaAddonGetFeatures", undefined),
    previewsGetArchiveFiles: (arg: previews.GetArchiveFilesArg) =>
      callApiV2("previewsGetArchiveFiles", arg).then((data) => {
        data.archive_files.forEach((file) => {
          file.preview = proxyPreviewMetadata(file.preview);
        });
        return data;
      }),
    previewsLoggedOutGetArchiveFiles: (arg: previews.GetArchiveFilesArg) =>
      callApiV2("previewsLoggedOutGetArchiveFiles", arg).then((data) => {
        data.archive_files.forEach((file) => {
          file.preview = proxyPreviewMetadata(file.preview);
        });
        return data;
      }),
    previewsGetPreviewDataBatch: (arg: previews.GetPreviewDataArg) =>
      callApiV2("previewsGetPreviewDataBatch", arg).then((data) => {
        data.results.forEach((result) => {
          result.preview = proxyPreviewMetadata(result.preview);
        });
        return data;
      }),
    transcriptEditGetTranscript: (arg: transcript_edit.GetTranscriptArg) =>
      callApiV2("transcriptEditGetTranscript", arg),
    transcriptEditCreateTranscript: (
      arg: transcript_edit.CreateTranscriptArg,
    ) => callApiV2("transcriptEditCreateTranscript", arg),
    transcriptEditUpdateTranscript: (
      arg: transcript_edit.UpdateTranscriptArg,
    ) => callApiV2("transcriptEditUpdateTranscript", arg),
  };
};
